import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { ApiError } from "../lib/api";
import { Brand } from "../components/Brand";
import { Constellation } from "../components/Constellation";
import { GoogleButton } from "../components/GoogleButton";

export default function Login() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      const next = (loc.state as { from?: string } | null)?.from ?? "/library";
      nav(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-6 py-16">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-10">
          <Brand className="text-2xl" />
          <p className="mt-6 font-body italic text-ink-300">
            Sign in to return to your observatory.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-sm border border-ink-700 bg-ink-800/60 backdrop-blur-sm p-8"
        >
          <div className="eyebrow mb-6">Sign in</div>

          <label className="field-label" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field mb-5"
            placeholder="you@example.com"
          />

          <label className="field-label" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field mb-6"
            placeholder="••••••••"
          />

          {error ? (
            <div className="mb-5 text-sm text-oxblood" role="alert">{error}</div>
          ) : null}

          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <GoogleButton label="signin_with" />

          <p className="mt-6 text-center text-sm text-ink-300">
            No account yet?{" "}
            <Link to="/register" className="text-gold-500 hover:text-gold-300">
              Create one
            </Link>
          </p>
        </form>

        <div className="mt-12 flex justify-center opacity-60">
          <Constellation />
        </div>
      </div>
    </div>
  );
}
